import { useLayoutEffect, useRef, useState } from 'react'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import { createPortal } from 'react-dom'
import { MenuRow, Divider } from './contextMenuChrome'
import type { ChartSettings } from './SettingsModal'
import { Z_MODAL } from './zLayers'

/* ------------------------------------------------------------------ */
/*  Price-scale right-click menu                                        */
/* ------------------------------------------------------------------ */

/**
 * A fixed-position context menu for the price (y) axis — a FAST PATH into the
 * price-scale settings already owned by SettingsModal. Every item toggles the
 * SAME `ChartSettings` object the modal edits (via `onChange`), so the menu is
 * never a separate source of truth (see the price-scale design doc).
 *
 * Chrome family matches SettingsModal / IndicatorSettingsDrawer: portaled to
 * <body>, rounded panel, border + shadow, small text rows, capture-phase
 * outside-click + Escape close.
 */
export interface PriceScaleContextMenuProps {
  /** Viewport coordinates of the right-click that opened the menu. */
  x: number
  y: number
  settings: ChartSettings
  /** Emits the next full ChartSettings — same callback the modal uses. */
  onChange: (next: ChartSettings) => void
  /** Release the price axis back to auto-fit (the "Auto" action). */
  onResetAuto: () => void
  /** Whether the price axis is currently auto-fitting — shows the Auto check. */
  isAuto: boolean
  /** Open the full SettingsModal ("More settings…"). */
  onMoreSettings: () => void
  /** "Add alert at $X": open the host's price alert popover at the price clicked.
   *  Renders only with `addAlertPrice`. */
  onAddAlert?: () => void
  /** That price at the chart's precision ("4.52"). */
  addAlertPrice?: string
  onClose: () => void
}

const SCALE_TYPE_OPTIONS: Array<{ value: ChartSettings['priceScaleType']; label: string }> = [
  { value: 'normal', label: 'Regular' },
  { value: 'percentage', label: 'Percent' },
  { value: 'log', label: 'Logarithmic' },
]

export function PriceScaleContextMenu({
  x,
  y,
  settings,
  onChange,
  onResetAuto,
  isAuto,
  onMoreSettings,
  onAddAlert,
  addAlertPrice,
  onClose,
}: PriceScaleContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Two-pass placement: render at the cursor, then clamp to the viewport once
  // the menu's measured size is known (so a right-click near an edge doesn't
  // push the menu off-screen). Hidden until measured.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el) return
    const margin = 8
    const w = el.offsetWidth
    const h = el.offsetHeight
    const left = Math.max(margin, Math.min(x, window.innerWidth - w - margin))
    const top = Math.max(margin, Math.min(y, window.innerHeight - h - margin))
    setPos({ left, top })
  }, [x, y])

  // Esc + deferred capture-phase outside-click close.
  usePortalDismiss(menuRef, onClose)

  // Build the next full ChartSettings and emit — same flow as the modal's `set`.
  const patch = (next: Partial<ChartSettings>) => onChange({ ...settings, ...next })

  const scopeCandlesOnly = settings.priceAxisScope === 'candles'
  const oppositeSide = settings.priceScalePosition === 'right' ? 'left' : 'right'

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      // Fixed-position panel portaled to <body> — same chrome as SettingsModal.
      className="fixed flex min-w-[220px] flex-col rounded-lg border border-primary/15 bg-popover py-1 text-[hsl(var(--chart-text))] shadow-2xl shadow-black/50"
      style={{
        zIndex: Z_MODAL,
        left: pos?.left ?? x,
        top: pos?.top ?? y,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {onAddAlert && addAlertPrice && (
        <>
          <MenuRow
            glyph={null}
            onSelect={() => {
              onAddAlert()
              onClose()
            }}
          >
            Add alert at ${addAlertPrice}
          </MenuRow>
          <Divider />
        </>
      )}
      <MenuRow glyph={isAuto ? 'check' : null} onSelect={onResetAuto}>
        Auto (fit data)
      </MenuRow>
      <MenuRow
        glyph={scopeCandlesOnly ? 'check' : null}
        onSelect={() => patch({ priceAxisScope: scopeCandlesOnly ? 'indicators' : 'candles' })}
      >
        Scale price chart only
      </MenuRow>

      <Divider />

      {SCALE_TYPE_OPTIONS.map((opt) => (
        <MenuRow
          key={opt.value}
          glyph={settings.priceScaleType === opt.value ? 'radio' : null}
          onSelect={() => patch({ priceScaleType: opt.value })}
        >
          {opt.label}
        </MenuRow>
      ))}
      <MenuRow
        glyph={settings.priceScaleInvert ? 'check' : null}
        onSelect={() => patch({ priceScaleInvert: !settings.priceScaleInvert })}
      >
        Invert scale
      </MenuRow>

      <Divider />

      <MenuRow glyph={null} onSelect={() => patch({ priceScalePosition: oppositeSide })}>
        {`Move scale to ${oppositeSide}`}
      </MenuRow>

      <Divider />

      <MenuRow glyph={null} onSelect={onMoreSettings}>
        More settings…
      </MenuRow>
    </div>
  )

  return createPortal(menu, document.body)
}
